/**
 * Axios 实例 & 请求方法封装
 * 提供统一的 API 请求接口，错误由拦截器统一处理
 */

import axios, { type AxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';
import { BASE_URL, TIMEOUT } from './config';
import { setupInterceptors } from './interceptors';
import { showToast } from '../../helpers/toast';

// 创建 axios 实例
const instance = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 设置拦截器
setupInterceptors(instance);

// ============ 类型定义 ============

// 通用响应结构
export interface ApiResponse<T = unknown> {
  success: boolean;
  code: number;
  data: T;
  message?: string;
}

// 请求错误类
export class ApiError extends Error {
  code: number;
  status: number;
  isNetworkError: boolean;

  constructor(message: string, code: number = -1, status: number = 500, isNetworkError = false) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.isNetworkError = isNetworkError;
  }
}

// ============ 辅助函数 ============

/**
 * 判断是否为网络错误
 */
const isNetworkError = (error: AxiosError): boolean => {
  return !error.response && !!error.request;
};

/**
 * 判断是否为超时错误
 */
const isTimeoutError = (error: AxiosError): boolean => {
  return error.code === 'ECONNABORTED' || error.message?.includes('timeout');
};

/**
 * 处理响应 - 成功时返回数据
 */
const handleResponse = <T>(response: AxiosResponse<ApiResponse<T>>): ApiResponse<T> => {
  const { data } = response;

  // 业务错误
  if (data.success === false) {
    throw new ApiError(data.message || '请求失败', data.code || -1, response.status);
  }

  return data;
};

/**
 * 处理错误 - 返回统一的错误响应结构
 * 错误 toast 已由拦截器处理，这里只需返回可安全使用的空数据
 */
const handleError = <T>(error: unknown): ApiResponse<T> => {
  // 如果是业务错误（ApiError），保留原始 message 并显示 toast
  if (error instanceof ApiError) {
    showToast.error(error.message);
    return {
      success: false,
      code: error.code,
      data: null as T,
      message: error.message,
    };
  }

  // 处理 Axios 错误
  const axiosError = error as AxiosError;
  const isNetwork = isNetworkError(axiosError);
  const isTimeout = isTimeoutError(axiosError);

  // 构建错误信息
  let message = '请求失败';
  let code = -1;
  let status = 0;

  if (isNetwork) {
    message = '网络连接失败';
    code = -2;
  } else if (isTimeout) {
    message = '请求超时';
    code = -3;
  } else if (axiosError.response) {
    status = axiosError.response.status;
    code = status;
    message = `请求失败 (${status})`;
  }

  // 返回失败的 ApiResponse 结构，而不是抛出异常
  // 这样页面组件可以安全地处理，不会导致未捕获的异常
  return {
    success: false,
    code,
    data: null as T,
    message,
  };
};

// ============ 请求方法 ============

/**
 * GET 请求
 */
export const get = async <T, P = Record<string, unknown>>(
  url: string,
  params?: P,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  try {
    const response = await instance.get<ApiResponse<T>>(url, { params, ...config });
    return handleResponse(response);
  } catch (error) {
    return handleError<T>(error);
  }
};

/**
 * POST 请求
 */
export const post = async <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  try {
    const response = await instance.post<ApiResponse<T>>(url, data, config);
    return handleResponse(response);
  } catch (error) {
    return handleError<T>(error);
  }
};

/**
 * PUT 请求
 */
export const put = async <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  try {
    const response = await instance.put<ApiResponse<T>>(url, data, config);
    return handleResponse(response);
  } catch (error) {
    return handleError<T>(error);
  }
};

/**
 * PATCH 请求
 */
export const patch = async <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  try {
    const response = await instance.patch<ApiResponse<T>>(url, data, config);
    return handleResponse(response);
  } catch (error) {
    return handleError<T>(error);
  }
};

/**
 * DELETE 请求
 */
export const del = async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  try {
    const response = await instance.delete<ApiResponse<T>>(url, config);
    return handleResponse(response);
  } catch (error) {
    return handleError<T>(error);
  }
};

// 导出 axios 实例（用于特殊场景）
export default instance;
